import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  HelpCircle,
  ChevronDown,
  Shield,
  FileText,
  Phone,
  BadgeCheck,
  ClipboardList,
} from 'lucide-react';
import { insuranceInfo, insuranceFaqs, firstVisitChecklist, siteConfig } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const InsuranceInfoPage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge badge-teal mb-4">Insurance & Billing</span>
            <h1 className="heading-hero mb-6">Insurance Information</h1>
            <p className="body-large">
              Insurance is accepted for eligible plans. Coverage and benefits vary by carrier and plan.
              Contact us to verify your benefits before your first visit.
            </p>
          </div>
        </div>
      </section>

      {/* Insurance Acceptance */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Content */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <BadgeCheck size={28} className="text-teal-600" />
                  <h2 className="heading-1">{insuranceInfo.headline}</h2>
                </div>
                <p className="body-large mb-6">
                  {insuranceInfo.shortDescription} {insuranceInfo.callToAction}
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Insurance Verification</p>
                      <p className="body-small text-gray-600">{insuranceInfo.verificationNote}</p>
                    </div>
                  </div>
                  {insuranceInfo.selfPayAvailable && (
                    <div className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Self-Pay Options</p>
                        <p className="body-small text-gray-600">{insuranceInfo.selfPayNote}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Transparent Pricing</p>
                      <p className="body-small text-gray-600">We explain your estimated costs before your first visit.</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setIsBookingOpen(true)}
                  className="btn-primary"
                >
                  Verify My Insurance
                  <ArrowRight size={16} />
                </button>
              </div>

              {/* Contact Card */}
              <div className="bg-teal-50 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Phone size={24} className="text-teal-600" />
                  <h3 className="heading-3">Questions About Coverage?</h3>
                </div>
                <p className="body-medium text-gray-700 mb-6">
                  Our team is happy to help verify your insurance benefits and answer 
                  any questions about coverage and costs.
                </p>
                <div className="space-y-3">
                  <a
                    href={`tel:${siteConfig.phone}`}
                    className="flex items-center gap-2 text-teal-700 hover:text-teal-800 font-medium"
                  >
                    <Phone size={16} />
                    <span className="font-mono">{siteConfig.phone}</span>
                  </a>
                  <p className="text-sm text-gray-600">{siteConfig.hours}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Process */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="badge badge-teal mb-4">The Process</span>
              <h2 className="heading-1 mb-4">What to Expect</h2>
              <p className="body-medium text-gray-600">
                Our simple process makes insurance verification easy and stress-free.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  step: "1",
                  title: "Schedule & Provide Info",
                  description: "Book your appointment and share your insurance information with us.",
                  icon: FileText,
                },
                {
                  step: "2",
                  title: "We Verify Benefits",
                  description: "Our team contacts your insurance to verify coverage and estimate costs.",
                  icon: Shield,
                },
                {
                  step: "3",
                  title: "Understand Your Costs",
                  description: "We explain your coverage and any out-of-pocket costs before your visit.",
                  icon: CheckCircle,
                },
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-lg font-bold text-teal-600">{item.step}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="body-small text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* First Visit Checklist */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="badge badge-teal mb-4">Prepare for Your Visit</span>
              <h2 className="heading-1 mb-4">What to Bring</h2>
              <p className="body-medium text-gray-600">
                Help your first appointment go smoothly by bringing these items.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <ClipboardList size={24} className="text-teal-600" />
                <h3 className="heading-3">First Visit Checklist</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {firstVisitChecklist.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="body-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="body-small text-gray-500">
                  <strong>Tip:</strong> Arriving 10-15 minutes early helps ensure a smooth check-in process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="badge badge-teal mb-4">FAQs</span>
              <h2 className="heading-1 mb-4">Common Questions</h2>
              <p className="body-medium text-gray-600">
                Answers to frequently asked questions about insurance and billing.
              </p>
            </div>

            <div className="space-y-4">
              {insuranceFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl overflow-hidden border border-gray-100"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                    className="w-full p-5 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-3">
                      <HelpCircle size={20} className="text-teal-600 flex-shrink-0" />
                      <span className="font-medium text-gray-900">{faq.question}</span>
                    </div>
                    <ChevronDown
                      size={20}
                      className={`text-gray-400 transition-transform duration-300 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-8 text-gray-600 body-small leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 mb-4">Ready to Get Started?</h2>
            <p className="body-large mb-8">
              Book your consultation and we'll help verify your insurance benefits.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsBookingOpen(true)}
                className="btn-primary"
              >
                Book a Consultation
                <ArrowRight size={16} />
              </button>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} sourcePage="insurance-info" />
    </>
  );
};

export default InsuranceInfoPage;
